import { OneFlowError, isAbort } from '../core/errors';
import { Priority } from '../core/types';
import type { AppError } from '../core/types';
import { mediaSourceCtor } from '../engine/capabilities';
import type { Engine } from '../engine/engine';
import { createObjectUrl, revokeObjectUrl } from '../engine/object-urls';
import type { SessionHandle } from '../engine/session-handle';

/**
 * Plays a COMPLETE file straight from OPFS. The Blob URL is file-backed, so
 * the browser reads from disk lazily instead of copying the file into memory.
 * Used for containers we do not segment ourselves (WebM, Ogg, WAV, FLAC, MOV…).
 * ALG-043
 */
export class FilePlayer {
  private url: string | null = null;
  private disposed = false;

  constructor(
    private readonly engine: Engine,
    private readonly id: string,
    private readonly media: HTMLMediaElement,
    private readonly owner: string,
  ) {}

  /** Start from `startAt`, and keep playing if the previous player was playing. */
  async start(startAt: number, autoplay = false): Promise<void> {
    const file = await this.engine.getCompleteFile(this.id);
    if (this.disposed) return;
    this.url = createObjectUrl(this.owner, file);
    this.media.src = this.url;
    if (startAt > 0 || autoplay) {
      const ready = () => {
        if (startAt > 0) this.media.currentTime = startAt;
        if (autoplay) void this.media.play().catch(() => undefined);
      };
      this.media.addEventListener('loadedmetadata', ready, { once: true });
    }
  }

  dispose(): void {
    this.disposed = true;
    this.media.removeAttribute('src');
    this.media.load();
    revokeObjectUrl(this.url);
    this.url = null;
  }
}

/** MPEG audio header → bitrate (kbps) for Layer III, MPEG-1 only (enough for a duration estimate). */
const MP3_BITRATES = [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 0];

export function estimateMp3Seconds(head: Uint8Array, totalBytes: number): number | null {
  let off = 0;
  if (head[0] === 0x49 && head[1] === 0x44 && head[2] === 0x33 && head.length >= 10) {
    off =
      10 +
      (((head[6]! & 0x7f) << 21) |
        ((head[7]! & 0x7f) << 14) |
        ((head[8]! & 0x7f) << 7) |
        (head[9]! & 0x7f));
  }
  for (let i = off; i + 4 < head.length && i < off + 4096; i++) {
    if (head[i] === 0xff && (head[i + 1]! & 0xe0) === 0xe0) {
      const version = (head[i + 1]! >> 3) & 3;
      const layer = (head[i + 1]! >> 1) & 3;
      const br = MP3_BITRATES[head[i + 2]! >> 4];
      if (version !== 3 || layer !== 1 || !br) return null;
      // A Xing/Info tag marks VBR, where a bitrate-based estimate is wrong.
      const tag = String.fromCharCode(...head.subarray(i + 4, Math.min(head.length, i + 4 + 40)));
      if (tag.includes('Xing') || tag.includes('Info') || tag.includes('VBRI')) return null;
      return ((totalBytes - off) * 8) / (br * 1000);
    }
  }
  return null;
}

export interface ProgressiveCallbacks {
  onError(error: AppError): void;
  onWaiting(message: string | null): void;
}

/**
 * Plays MP3 while it downloads by appending the raw frames to a SourceBuffer
 * in order. Seeking beyond what is saved unlocks once the file is complete and
 * the UI swaps to FilePlayer. ARCH-008, ALG-042
 */
export class ProgressiveAudioPlayer {
  private ms: MediaSource | null = null;
  private sb: SourceBuffer | null = null;
  private url: string | null = null;
  private abort = new AbortController();
  private disposed = false;
  private pos = 0;
  private readonly chunk = 192 * 1024;
  private readonly targetAhead = 40;

  constructor(
    private readonly handle: SessionHandle,
    private readonly audio: HTMLAudioElement,
    private readonly owner: string,
    private readonly cb: ProgressiveCallbacks,
    private readonly mime: string,
  ) {}

  static supported(mime: string): boolean {
    return mediaSourceCtor()?.isTypeSupported(mime) ?? false;
  }

  async start(): Promise<void> {
    const Ctor = mediaSourceCtor();
    if (!Ctor) throw new OneFlowError('DECODE_UNSUPPORTED');
    const ms = new Ctor();
    this.ms = ms;
    this.url = createObjectUrl(this.owner, ms);
    this.audio.src = this.url;
    await new Promise<void>((resolve) =>
      ms.addEventListener('sourceopen', () => resolve(), { once: true }),
    );
    if (this.disposed) return;
    const sb = ms.addSourceBuffer(this.mime);
    sb.mode = 'sequence';
    this.sb = sb;
    void this.pump();
  }

  private ahead(): number {
    const t = this.audio.currentTime;
    const b = this.audio.buffered;
    for (let i = 0; i < b.length; i++)
      if (b.start(i) <= t + 0.3 && b.end(i) >= t) return b.end(i) - t;
    return 0;
  }

  private async pump(): Promise<void> {
    const signal = this.abort.signal;
    try {
      for (;;) {
        if (this.disposed) return;
        const total = this.handle.total;
        if (total !== null && this.pos >= total) break;
        while (this.ahead() > this.targetAhead && !this.disposed) {
          await new Promise((r) => setTimeout(r, 400));
        }
        const end = total !== null ? Math.min(total, this.pos + this.chunk) : this.pos + this.chunk;
        if (!this.handle.isReadable({ start: this.pos, end }))
          this.cb.onWaiting('Waiting for more of the file…');
        const bytes = await this.handle.ensure(
          { start: this.pos, end },
          {
            priority: this.ahead() < 4 ? Priority.PlaybackEmergency : Priority.ImminentPlayback,
            reason: 'audio-media',
            signal,
          },
        );
        this.cb.onWaiting(null);
        const sb = this.sb!;
        while (sb.updating)
          await new Promise((r) => sb.addEventListener('updateend', () => r(null), { once: true }));
        const copy = bytes.slice();
        sb.appendBuffer(copy.buffer);
        await new Promise<void>((resolve, reject) => {
          sb.addEventListener('updateend', () => resolve(), { once: true });
          sb.addEventListener('error', () => reject(new OneFlowError('PREVIEW_FAILED')), {
            once: true,
          });
        });
        this.pos += bytes.length;
      }
      if (this.ms?.readyState === 'open') this.ms.endOfStream();
    } catch (e) {
      if (isAbort(e) || this.disposed) return;
      this.cb.onError(
        e instanceof OneFlowError ? e.appError : new OneFlowError('PREVIEW_FAILED').appError,
      );
    }
  }

  dispose(): void {
    this.disposed = true;
    this.abort.abort();
    this.audio.removeAttribute('src');
    this.audio.load();
    revokeObjectUrl(this.url);
    this.url = null;
    this.sb = null;
    this.ms = null;
  }
}
